import React, { useEffect } from "react";
import { Checkbox, FormControlLabel, Grid, Stack, TextField, Typography } from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import { zodResolver } from "@hookform/resolvers/zod";
import { FormModal } from "@packsize/components";

import { type ClassificationConfiguration, classificationConfigurationSchema } from "@/schemas";

export interface ClassificationConfigurationModalTemplateProps {
	readonly open: boolean;
	readonly defaultValues?: ClassificationConfiguration;
	readonly errorMessage?: string;
	readonly onSubmit: (data: ClassificationConfiguration) => Promise<void>;
	readonly onClose: () => void;
}

export const ClassificationConfigurationModalTemplate: React.FC<ClassificationConfigurationModalTemplateProps> = ({
	open,
	defaultValues,
	errorMessage,
	onClose,
	onSubmit,
}) => {
	const intl = useIntl();

	const {
		register,
		handleSubmit,
		reset,
		control,
		formState: { errors, isSubmitting },
	} = useForm<ClassificationConfiguration>({
		resolver: zodResolver(classificationConfigurationSchema(intl)),
		defaultValues: defaultValues ?? {
			number: 0,
			alias: "",
			isPriorityLabel: false,
		},
	});

	useEffect(() => {
		if (open !== false) {
			return;
		}

		reset(defaultValues);
	}, [defaultValues, open, reset]);

	return (
		<FormModal
			open={open}
			title={
				defaultValues
					? intl.formatMessage({ id: "Edit Classification", defaultMessage: "Edit Classification" })
					: intl.formatMessage({ id: "Create Classification", defaultMessage: "Create Classification" })
			}
			onSubmit={handleSubmit(onSubmit)}
			size="sm"
			submitText={intl.formatMessage({ id: "Save", defaultMessage: "Save" })}
			cancelText={intl.formatMessage({ id: "Cancel", defaultMessage: "Cancel" })}
			onClose={onClose}
			onCancel={onClose}
			slotProps={{
				confirmButton: {
					loading: isSubmitting,
					"data-testid": "classification-configuration-modal__button__confirm",
				},
				cancelButton: {
					"data-testid": "classification-configuration-modal__button__cancel",
				},
			}}
		>
			<Grid container gap={2} direction="column">
				<TextField
					label={intl.formatMessage({ id: "Alias", defaultMessage: "Alias" })}
					error={Boolean(errors?.alias)}
					helperText={errors?.alias?.message}
					{...register("alias")}
					slotProps={{
						htmlInput: {
							"data-testid": "classification-configuration-modal__input__alias",
						},
						formHelperText: {
							"data-testid": "classification-configuration-modal__input__alias-error",
						},
					}}
				/>
				<TextField
					label={intl.formatMessage({ id: "Number", defaultMessage: "Number" })}
					type="number"
					error={Boolean(errors?.number)}
					helperText={errors?.number?.message}
					{...register("number", { valueAsNumber: true })}
					slotProps={{
						htmlInput: {
							"data-testid": "classification-configuration-modal__input__number",
							min: 0,
							step: 1,
						},
						formHelperText: {
							"data-testid": "classification-configuration-modal__input__number-error",
						},
					}}
				/>
				<Controller
					name="isPriorityLabel"
					control={control}
					render={({ field }) => (
						<FormControlLabel
							control={
								<Checkbox
									{...field}
									checked={field.value}
									data-testid="classification-configuration-modal__input__is-priority-label"
								/>
							}
							label={intl.formatMessage({ id: "Is Priority Label", defaultMessage: "Is Priority Label" })}
						/>
					)}
				/>

				{Boolean(errorMessage) === true && (
					<Stack>
						<Typography color="error">
							<FormattedMessage id="Failed to save Classification" defaultMessage="Failed to save Classification" />
						</Typography>
						<Typography color="error" data-testid="classification-configuration-modal__save__error">
							{errorMessage}
						</Typography>
					</Stack>
				)}
			</Grid>
		</FormModal>
	);
};
